import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, BoxProps } from '@mui/material';
import { alpha } from '@mui/system';
import MUIButton, { ButtonProps } from '@mui/material/Button';
import { Icon, IconName } from './icon';
import theme from '../theme';

export type Props = Omit<ButtonProps, 'variant' | 'color'> & {
  text?: string;
  disabled?: boolean;
  icon?: IconName;
  variant?: 'primary' | 'ghost' | 'outline' | 'inline';
  color?: 'marigold' | 'white' | 'cyan';
  textSx?: BoxProps['sx']; // Style overrides for the button text Box.
};

const isValidColor = (color?: string) =>
  color &&
  (color === 'marigold' ||
    color === 'white' ||
    color === 'cyan' ||
    color === 'grey');

const StyledButton = styled(MUIButton as React.FC<Props>, {
  skipVariantsResolver: true,
})<Props>(({ variant, color }: Props) => {
  const buttonColors = {
    marigold: theme.palette.marigold,
    cyan: theme.palette.cyan,
    white: theme.palette.greyscale.white,
    grey: theme.palette.greyscale.mediumGrey,
  };
  const bgColor = buttonColors[color ?? 'marigold'];
  if (variant === 'ghost')
    return {
      color: bgColor ?? theme.palette.greyscale.white,
      '&:hover': {
        backgroundColor: isValidColor(color)
          ? alpha(bgColor, 0.06)
          : 'rgba(106, 202, 216, 0.06)',
        color: bgColor ?? theme.palette.cyan,
      },
      '&:active, &:focus': {
        backgroundColor: isValidColor(color)
          ? alpha(bgColor, 0.12)
          : 'rgba(106, 202, 216, 0.12)',
        color: bgColor ?? theme.palette.cyan,
      },
      '&:disabled': {
        color: 'linear-gradient(0deg, #FFFFFF, #FFFFFF)',
        backgroundColor:
          'linear-gradient(0deg, rgba(0, 0, 0, 0.38), rgba(0, 0, 0, 0.38))',
      },
    };
  if (variant === 'outline')
    return {
      color: bgColor ?? theme.palette.marigold,
      border: `1px solid ${bgColor ?? theme.palette.marigold}`,
      '&:hover': {
        backgroundColor: isValidColor(color)
          ? alpha(bgColor, 0.04)
          : 'rgba(255, 173, 0, 0.04)',
      },
      '&active, &:focus': {
        backgroundColor: isValidColor(color)
          ? alpha(bgColor, 0.12)
          : 'rgba(255, 173, 0, 0.12)',
      },
      '&:disabled': {
        color: theme.palette.greyscale.mediumGrey,
        borderColor: theme.palette.greyscale.mediumGrey,
      },
    };

  if (variant === 'inline')
    return {
      color: isValidColor(color) ? buttonColors[color] : theme.palette.cyan,
      padding: `${0}!important`, // Ugly, but without !important the inline style in the component is overriding this ¯\_(ツ)_/¯
      margin: 0,
      background: 'transparent',
      '.MuiBox-root': { padding: 0, margin: 0 },
      '&:hover': {
        color: alpha(buttonColors[color], 0.75),
        background: 'transparent',
      },
      '&:active, &:focus': {
        background: 'transparent',
      },
    };

  return {
    backgroundColor: isValidColor(color) ? bgColor : theme.palette.marigold,
    color: `${theme.palette.greyscale.black}!important`, // MUI was overriding this with white for reasons unknown.
    '&:hover': {
      backgroundColor: bgColor ?? theme.palette.marigold,
      opacity: 0.85,
    },
    '&:active, &:focus': {
      backgroundColor: bgColor ?? theme.palette.marigold,
      opacity: 0.85,
    },
    '&:disabled': {
      color: theme.palette.greyscale.black,
      backgroundColor: theme.palette.greyscale.mediumGrey,
    },
  };
});

const Button: React.FC<Props> = ({
  text,
  icon,
  variant,
  sx,
  textSx,
  color,
  ...props
}) => {
  const [isClient, setIsClient] = useState('server');

  useEffect(() => {
    setIsClient('client');
  }, []);

  return (
    <StyledButton
      key={isClient}
      variant={variant ?? 'primary'}
      color={color}
      sx={{ padding: '6px 6px 6px 8px', ...sx }}
      {...props}
    >
      {typeof icon !== 'undefined' && (
        <Icon
          iconName={icon ?? 'add'}
          size="24px"
          sx={{ marginRight: '6px' }}
        />
      )}
      {text && <Box sx={{ padding: '2px 8px 2px 6px', ...textSx }}>{text}</Box>}
    </StyledButton>
  );
};

export default Button;
